// @ts-expect-error React import required to render in Jira SSR
import React from 'react';
import {
	type PlacementMessageFrontendFactory,
	type FrontendStage,
	type NoProps,
	type PlacementRenderer,
	type AnyProps,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { useState, type ReactNode } from 'react';

import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import { usePlacementApi } from '@post-office/use-placement-api';

import type { CustomRenderer } from '@post-office/message-maker-browser/types';

export type TourDelivery = {
	onFinish: (outcome: 'completed' | 'dismissed', step: number) => void;
	onInteraction: (action: 'next' | 'back', step: number) => void;
};

export type ContentDelivery = Pick<TourDelivery, 'onFinish'>;

export type AppProps = {
	renderTour?: (delivery: TourDelivery) => ReactNode;
	/**
	 * Optional host-rendered content for the `jira-wiv-beta-changeboarding` message. When provided,
	 * Post Office still decides when the message renders, but the host renders the UI instead of the
	 * built-in step tour and reports completion through `delivery.onFinish`.
	 */
	renderContent?: (delivery: ContentDelivery) => ReactNode;
	onDismiss?: () => void;
};

export type MessageTemplateContext = {
	message: string;
	/**
	 * Automatic experience selected by Post Office. Absent when `wiv_thor_ga_change-boarding` is off,
	 * in which case the built-in step tour renders.
	 */
	experience?: 'onboarding' | 'changeboarding';
};

type HostRenderers = Pick<AppProps, 'renderTour' | 'renderContent'>;

/**
 * Selects the host renderer for a message from its `experience`:
 * - `onboarding` → `renderTour` (step-wise spotlight tour)
 * - `changeboarding` → `renderContent` (modal/banner)
 * - no experience → no host renderer (built-in step tour)
 *
 * Returns `null` when the host cannot render the selected experience.
 */
const selectHostRenderers = (
	{ experience }: MessageTemplateContext,
	{ renderTour, renderContent }: HostRenderers,
): HostRenderers | null => {
	if (experience === 'onboarding') {
		return renderTour ? { renderTour } : null;
	}
	if (experience === 'changeboarding') {
		return renderContent ? { renderContent } : null;
	}
	return {};
};

export type PlacementRenderProps = AppProps;

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

const placementConfig: PlacementBrowserConfig = {
	placementId: 'wiv-beta-changeboarding',
	options: { choreographer: { isDefaultEnabled: true } },
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	AppProps
> = createFrontend<AppProps>((Messages) => ({ onDismiss, renderTour, renderContent }) => {
	const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
	const { data, error, isLoading } = usePlacementApi<{
		messages: (Omit<EligibleMessage<MessageTemplateContext>, 'context'> & MessageTemplateContext)[];
	}>('/');

	if (error) {
		throw error;
	}

	const visibleMessages = data?.messages
		?.filter((message) => !dismissedIds.has(message.messageInstanceId))
		.flatMap((message) => {
			const renderers = selectHostRenderers(message, { renderTour, renderContent });
			return renderers ? [{ message, renderers }] : [];
		})
		.slice(0, 1);

	const handleDismiss = (messageInstanceId: string) => {
		setDismissedIds(
			(previousDismissedIds) => new Set([...Array.from(previousDismissedIds), messageInstanceId]),
		);
		onDismiss?.();
	};

	if (isLoading || !visibleMessages || visibleMessages.length === 0) {
		return null;
	}

	return (
		<>
			{visibleMessages.map(({ message, renderers }) => (
				<Messages.render
					key={message.messageInstanceId}
					{...renderers}
					onDismiss={() => handleDismiss(message.messageInstanceId)}
					{...message}
				/>
			))}
		</>
	);
});
